import React from 'react';

export const ContentSizeFitterBehavior = {
  id: 'ContentSizeFitter',
  label: 'Content Size Fitter',
  icon: 'Maximize',
  description: 'Controls the size of its own layout element based on child bounds.',
  isLayoutProvider: false,
  needsChildInfo: true, // Flag to indicate this behavior needs child information
  fields: [
    {
      name: 'horizontalFit',
      label: 'Horizontal Fit',
      type: 'select',
      options: ['Off', 'On'],
      default: 'Off'
    },
    {
      name: 'verticalFit',
      label: 'Vertical Fit',
      type: 'select',
      options: ['Off', 'On'],
      default: 'Off'
    },
  ],
  getStyle: (props, isLiveMode, node, allNodes, availableW, availableH) => {
    const style = {};
    const horizontalFit = props.horizontalFit || 'Off';
    const verticalFit = props.verticalFit || 'Off';

    // When enabled, calculate bounding box of all descendants
    if ((horizontalFit === 'On' || verticalFit === 'On') && node && allNodes) {
      // Skip during SSR - document is not available
      if (typeof document === 'undefined') {
        return style;
      }

      // Helper to resolve anchored rect to actual pixels
      const resolveAnchoredRect = (child, parentWidth, parentHeight, ignoreHorizontalPercentage = false, ignoreVerticalPercentage = false) => {
        const anchors = child.anchors || { minX: 0, minY: 0, maxX: 0, maxY: 0 };

        // When ignorePercentage is true for an axis, treat ALL percentage anchors as 0
        // This gives us absolute positioning relative to parent origin
        const effectiveMinX = ignoreHorizontalPercentage ? 0 : anchors.minX;
        const effectiveMinY = ignoreVerticalPercentage ? 0 : anchors.minY;
        const effectiveMaxX = ignoreHorizontalPercentage ? 0 : anchors.maxX;
        const effectiveMaxY = ignoreVerticalPercentage ? 0 : anchors.maxY;

        // Calculate actual position and size based on anchors
        const left = (parentWidth * effectiveMinX) + (child.x || 0);
        const top = (parentHeight * effectiveMinY) + (child.y || 0);
        const width = (parentWidth * (effectiveMaxX - effectiveMinX)) + (child.width || 0);
        const height = (parentHeight * (effectiveMaxY - effectiveMinY)) + (child.height || 0);

        return { left, top, width, height };
      };

      // Get padding values from Grid behavior if present
      const getContainerPadding = (node) => {
        const gridComp = node.attachedComponents?.find(c => c.type === 'Grid');
        if (!gridComp) return { left: 0, right: 0, top: 0, bottom: 0 };

        const props = gridComp?.props || {};
        return {
          left: Number(props.paddingLeft) || 0,
          right: Number(props.paddingRight) || 0,
          top: Number(props.paddingTop) || 0,
          bottom: Number(props.paddingBottom) || 0
        };
      };

      // Calculate bounds of immediate children only
      const calculateImmediateChildrenBounds = (nodeId, offsetX = 0, offsetY = 0, parentWidth = 0, parentHeight = 0, ignoreHorPercentage = false, ignoreVerPercentage = false) => {
        const children = allNodes.filter(n => n.parentId === nodeId);
        const bounds = [];

        children.forEach(child => {
          // Resolve anchored position
          // For the top-level children of a ContentSizeFitter node, ignore percentage anchors in the fitted direction
          let rect = resolveAnchoredRect(child, parentWidth, parentHeight, ignoreHorPercentage, ignoreVerPercentage);

          // Try to get actual rendered dimensions and position from the DOM
          let el = document.getElementById(child.id);
          if (!el) el = document.getElementById(`vbecs-${child.id}`);
          
          let containerEl = document.getElementById(nodeId);
          if (!containerEl) containerEl = document.getElementById(`vbecs-${nodeId}`);

          if (el && containerEl) {
            const domRect = el.getBoundingClientRect();
            const containerRect = containerEl.getBoundingClientRect();

            // Calculate position relative to container
            const domRelativeLeft = domRect.left - containerRect.left;
            const domRelativeTop = domRect.top - containerRect.top;

            // Use DOM dimensions and position (most accurate)
            // This respects all behaviors that have already been applied (Grid, etc.)
            rect = {
              left: domRelativeLeft,
              top: domRelativeTop,
              width: domRect.width,
              height: domRect.height
            };
          }

          const absX = offsetX + rect.left;
          const absY = offsetY + rect.top;
          const absRight = absX + rect.width;
          const absBottom = absY + rect.height;

          // Include ALL immediate children bounds (not recursing deeper)
          bounds.push({
            minX: absX,
            minY: absY,
            maxX: absRight,
            maxY: absBottom,
            name: child.name || child.id
          });
        });

        return bounds;
      };

      // Start calculation with available space from parent if provided, otherwise fallback
      const initialWidth = availableW ?? (horizontalFit === 'On' ? (node.width || 0) : 10000);
      const initialHeight = availableH ?? (verticalFit === 'On' ? (node.height || 0) : 10000);

      // Get padding from any Grid behavior
      const padding = getContainerPadding(node);

      // Only ignore percentage anchors on the axis being fitted
      const ignoreHor = false; // Never ignore horizontal - let children resolve horizontally
      const ignoreVer = verticalFit === 'On'; // Ignore vertical only when fitting vertically
      const allBounds = calculateImmediateChildrenBounds(node.id, 0, 0, initialWidth, initialHeight, ignoreHor, ignoreVer);

      if (allBounds.length > 0) {
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        allBounds.forEach(bound => {
          minX = Math.min(minX, bound.minX);
          minY = Math.min(minY, bound.minY);
          maxX = Math.max(maxX, bound.maxX);
          maxY = Math.max(maxY, bound.maxY);
        });

        // Set dimensions based on bounding box - only apply if the fit is enabled
        // The size should be from origin (0,0) to the furthest edge (maxX, maxY)
        // This ensures the container is large enough to contain all children at their positions
        // Add padding to account for container padding
        if (horizontalFit === 'On' && maxX > minX) {
          const fittedWidth = maxX + padding.right;
          style.width = `${fittedWidth}px`;
        }

        if (verticalFit === 'On' && maxY > minY) {
          const fittedHeight = maxY + padding.bottom;
          style.height = `${fittedHeight}px`;
        }
      }
    }

    return style;
  }
};
